import React, { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useDraftManual from '../_data_sources/useDraftManual';
import {
  UpdateDraftManual,
  Attachment,
  PolicyHeaderDateFormat,
  PolicyHeaderDateFillOption,
  PolicyHeaderIssuingAuthorityType,
  PolicyHeaderOrderItem,
  PolicyHeaderFieldType,
} from '../../_types/manuals/draft';

// Constants
const EMPTY_CUSTOM_FIELD = { header: '', value: '' } as const;
const DEFAULT_CUSTOM_FIELDS = [EMPTY_CUSTOM_FIELD, EMPTY_CUSTOM_FIELD] as const;

// Helper function to convert backend boolean to frontend option
const mapBackendToFrontend = (autoSet: boolean | undefined): PolicyHeaderDateFillOption => {
  if (autoSet === true) return 'AUTO_FILL_ONLY';
  if (autoSet === false) return 'MANUAL_SELECT_ONLY';
  return 'AUTO_FILL_ONLY'; // default
};

// Helper function to normalize custom fields to exactly 2 elements
const normalizeCustomFields = (
  fields?: Array<{ header: string; value: string }>
): Array<{ header: string; value: string }> => [
  fields?.[0] ?? { header: '', value: '' },
  fields?.[1] ?? { header: '', value: '' },
];

// Helper function to disable custom field in header order when title is empty
const updateHeaderOrderForCustomField = (
  order: PolicyHeaderOrderItem[],
  customIndex: number,
  hasHeader: boolean
): PolicyHeaderOrderItem[] => {
  if (hasHeader) return order;

  const fieldType: PolicyHeaderFieldType = customIndex === 0 ? 'CUSTOM1' : 'CUSTOM2';
  return order.map(item =>
    item.type === fieldType && item.state === 'ENABLED'
      ? { ...item, state: 'DISABLED' as const }
      : item
  );
};

// Helper function to generate default header order
const getDefaultHeaderOrder = (customFields?: Array<{ header: string; value: string; }>): PolicyHeaderOrderItem[] => {
  return [
    { type: 'EFFECTIVE_DATE', state: 'ENABLED' },
    { type: 'REVISED_DATE', state: 'ENABLED' },
    { type: 'AGENCY_LOGO', state: 'DISABLED' },
    { type: 'ISSUING_AUTHORITY', state: 'DISABLED' },
    {
      type: 'CUSTOM1',
      state: customFields?.[0]?.header ? 'ENABLED' : 'DISABLED'
    },
    {
      type: 'CUSTOM2',
      state: customFields?.[1]?.header ? 'ENABLED' : 'DISABLED'
    },
  ];
};

interface ManualPropertiesContextProps {
  isLoading: boolean;
  isError: boolean;
  vertical: string;
  draftManual: UpdateDraftManual;
  attachments: Attachment[];
  localAttachments: Attachment[];
  setLocalAttachments: (attachments: Attachment[]) => void;
  // Policy Headers - State
  headerState: 'ENABLED' | 'DISABLED';
  setHeaderState: (state: 'ENABLED' | 'DISABLED') => void;
  dateFormat: PolicyHeaderDateFormat;
  setDateFormat: (format: PolicyHeaderDateFormat) => void;
  effectiveDateOption: PolicyHeaderDateFillOption;
  setEffectiveDateOption: (option: PolicyHeaderDateFillOption) => void;
  revisedDateOption: PolicyHeaderDateFillOption;
  setRevisedDateOption: (option: PolicyHeaderDateFillOption) => void;
  issuingAuthorityType: PolicyHeaderIssuingAuthorityType;
  setIssuingAuthorityType: (type: PolicyHeaderIssuingAuthorityType) => void;
  headerOrder: PolicyHeaderOrderItem[];
  setHeaderOrder: (order: PolicyHeaderOrderItem[]) => void;
  // Policy Headers - Default values
  agencyLogoId: string;
  setAgencyLogoId: (id: string) => void;
  issuingAuthoritySignatureId: string;
  setIssuingAuthoritySignatureId: (id: string) => void;
  issuingAuthorityName: string;
  setIssuingAuthorityName: (name: string) => void;
  customFields: Array<{ header: string; value: string; }>;
  setCustomFields: React.Dispatch<React.SetStateAction<Array<{ header: string; value: string; }>>>;
  // Policy Headers - Handlers
  handleUpdateAgencyLogo: (attachmentId: string) => void;
  handleUpdateIssuingAuthoritySignature: (attachmentId: string) => void;
  handleUpdateIssuingAuthorityName: (name: string) => void;
  handleUpdateCustomField: (index: number, field: { header: string; value: string; }) => void;
  handleToggleVisibility: (orderType: PolicyHeaderFieldType) => void;
  handleAttachmentUploaded: (newAttachment: Attachment) => void;
}

const ManualPropertiesContext = createContext<ManualPropertiesContextProps | undefined>(undefined);

export const ManualPropertiesProvider: React.FC<{ children: React.ReactNode; }> = ({ children }) => {
  const { id } = useParams();
  const { isFetching, isError, data } = useDraftManual(id);

  // Policy Headers State
  const [headerState, setHeaderState] = useState<'ENABLED' | 'DISABLED'>('DISABLED');
  const [dateFormat, setDateFormat] = useState<PolicyHeaderDateFormat>('MMDDYYYY');
  const [effectiveDateOption, setEffectiveDateOption] = useState<PolicyHeaderDateFillOption>('AUTO_FILL_ONLY');
  const [revisedDateOption, setRevisedDateOption] = useState<PolicyHeaderDateFillOption>('AUTO_FILL_ONLY');
  const [issuingAuthorityType, setIssuingAuthorityType] = useState<PolicyHeaderIssuingAuthorityType>('SIGNATURE_AND_NAME');
  const [headerOrder, setHeaderOrder] = useState<PolicyHeaderOrderItem[]>([]);

  // Default values state
  const [agencyLogoId, setAgencyLogoId] = useState<string>('');
  const [issuingAuthoritySignatureId, setIssuingAuthoritySignatureId] = useState<string>('');
  const [issuingAuthorityName, setIssuingAuthorityName] = useState<string>('');
  const [customFields, setCustomFields] = useState<Array<{ header: string; value: string; }>>([
    { ...EMPTY_CUSTOM_FIELD },
    { ...EMPTY_CUSTOM_FIELD },
  ]);
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>([]);

  // Sync local attachments with data
  useEffect(() => {
    if (data?.attachments) {
      setLocalAttachments(data.attachments);
    }
  }, [data?.attachments]);

  // Initialize policy headers state from draft manual data
  useEffect(() => {
    if (data?.id) {
      const headerConfig = data.headerConfiguration;
      if (headerConfig) {
        setHeaderState(headerConfig.state ?? 'DISABLED');
        // Use the same format for both effective and revised dates
        const format = headerConfig.effectiveDateFormat ?? headerConfig.revisedDateFormat ?? 'MMDDYYYY';
        setDateFormat(format);
        // Map backend booleans to frontend options
        setEffectiveDateOption(mapBackendToFrontend(headerConfig.autoSetEffectiveDate));
        setRevisedDateOption(mapBackendToFrontend(headerConfig.autoSetRevisedDate));
        setIssuingAuthorityType(headerConfig.issuingAuthorityType ?? 'SIGNATURE_AND_NAME');

        // Initialize default values
        setAgencyLogoId(headerConfig.agencyLogo ?? '');
        setIssuingAuthoritySignatureId(headerConfig.issuingAuthoritySignature ?? '');
        setIssuingAuthorityName(headerConfig.issuingAuthorityName ?? '');
        const normalizedCustomFields = normalizeCustomFields(headerConfig.customFields);
        setCustomFields(normalizedCustomFields);

        // Initialize header order - disable custom fields with blank titles
        if (headerConfig.order && headerConfig.order.length > 0) {
          let updatedOrder = headerConfig.order;
          updatedOrder = updateHeaderOrderForCustomField(updatedOrder, 0, !!normalizedCustomFields[0].header);
          updatedOrder = updateHeaderOrderForCustomField(updatedOrder, 1, !!normalizedCustomFields[1].header);
          setHeaderOrder(updatedOrder);
        } else {
          // No header order exists - create default order
          setHeaderOrder(getDefaultHeaderOrder(normalizedCustomFields));
        }
      } else {
        // Initialize with default order if no header config exists
        setHeaderOrder(getDefaultHeaderOrder());
      }
    }
  }, [data]);

  // Handler functions
  const handleUpdateAgencyLogo = (attachmentId: string) => {
    setAgencyLogoId(attachmentId);
  };

  const handleUpdateIssuingAuthoritySignature = (attachmentId: string) => {
    setIssuingAuthoritySignatureId(attachmentId);
  };

  const handleUpdateIssuingAuthorityName = (name: string) => {
    setIssuingAuthorityName(name);

    // Auto-disable issuing authority header if name is cleared while type requires name
    const requiresName = issuingAuthorityType === 'SIGNATURE_AND_NAME' || issuingAuthorityType === 'NAME_ONLY';
    if (!name && requiresName) {
      setHeaderOrder(prevOrder =>
        prevOrder.map(item =>
          item.type === 'ISSUING_AUTHORITY' && item.state === 'ENABLED'
            ? { ...item, state: 'DISABLED' as const }
            : item
        )
      );
    }
  };

  const handleUpdateCustomField = (index: number, field: { header: string; value: string; }) => {
    setCustomFields(prev => {
      const updated = [...prev];
      // Ensure array has at least 2 elements
      while (updated.length < 2) {
        updated.push({ ...EMPTY_CUSTOM_FIELD });
      }
      updated[index] = field;

      // Auto-disable custom field if title is cleared while enabled
      if (!field.header) {
        setHeaderOrder(prevOrder =>
          updateHeaderOrderForCustomField(prevOrder, index, false)
        );
      }

      return updated;
    });
  };

  const handleToggleVisibility = (orderType: PolicyHeaderFieldType) => {
    setHeaderOrder(prev => {
      return prev.map(item => {
        if (item.type === orderType) {
          const newState = item.state === 'ENABLED' ? 'DISABLED' : 'ENABLED';
          return { ...item, state: newState };
        }
        return item;
      });
    });
  };

  const handleAttachmentUploaded = (newAttachment: Attachment) => {
    setLocalAttachments(prev => [...prev, newAttachment]);
  };

  const contextValues = {
    isLoading: isFetching,
    isError: isError,
    vertical: 'Vertical',
    draftManual: {
      id: data?.id || '',
      name: data?.name || '',
      manualTypeId: data?.manualTypeId || '',
      description: data?.description || '',
      manualLevel: data?.manualLevel || '',
      jurisdiction: data?.jurisdiction?.name || '',
      verticalId: data?.verticalId || '',
      headerConfiguration: data?.headerConfiguration,
      siteKey: data?.siteKey || '',
      publishedAt: data?.publishedAt || '',
    },
    attachments: data?.attachments || [],
    localAttachments,
    setLocalAttachments,
    // Policy Headers - State
    headerState,
    setHeaderState,
    dateFormat,
    setDateFormat,
    effectiveDateOption,
    setEffectiveDateOption,
    revisedDateOption,
    setRevisedDateOption,
    issuingAuthorityType,
    setIssuingAuthorityType,
    headerOrder,
    setHeaderOrder,
    // Policy Headers - Default values
    agencyLogoId,
    setAgencyLogoId,
    issuingAuthoritySignatureId,
    setIssuingAuthoritySignatureId,
    issuingAuthorityName,
    setIssuingAuthorityName,
    customFields,
    setCustomFields,
    // Policy Headers - Handlers
    handleUpdateAgencyLogo,
    handleUpdateIssuingAuthoritySignature,
    handleUpdateIssuingAuthorityName,
    handleUpdateCustomField,
    handleToggleVisibility,
    handleAttachmentUploaded,
  };

  return <ManualPropertiesContext.Provider value={contextValues}>{children}</ManualPropertiesContext.Provider>;
};

export const useManualPropertiesContext = () => {
  const context = useContext(ManualPropertiesContext);
  if (!context) {
    throw new Error('useManualPropertiesContext must be used within a ManualPropertiesProvider');
  }
  return context;
};
